import { useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { useApolloClient } from '@apollo/client'
import {
  F0AvatarPerson,
  F0Box,
  F0Button,
  F0Dialog,
  F0Heading,
  F0Link,
  F0TagStatus,
  F0Text,
} from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'

import {
  ExpenseBudgetAllocationFragment,
  FinanceBudgetsDetailBudgetFragment,
} from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { amountWithCurrency } from 'modules/finance/lib/budgets'
import { useBulkUpdateBudgetAllocation } from 'modules/finance/hooks/useBudgetAllocations'

import { RemoveEmployeeModal } from './RemoveEmployeeModal'

interface Props {
  item: ExpenseBudgetAllocationFragment
  budget: FinanceBudgetsDetailBudgetFragment
  onClose: () => void
}

export const EmployeeDetail = ({ item, budget, onClose }: Props) => {
  const apolloClient = useApolloClient()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const employee = item.employee

  const periodStart = budget.periodStart ? dateBackendDateTimeToUI(budget.periodStart) : null
  const periodEnd = budget.periodEnd ? dateBackendDateTimeToUI(budget.periodEnd) : null
  const period = [periodStart, periodEnd].filter(Boolean).join(' - ')

  const recordsUrl = navigation.finance.spending.expenses.budgets.detail.records.fullPath({
    budgetId: budget.id,
  })

  const handleGoToRecords = () => {
    onClose()
    navigate(recordsUrl)
  }

  // State for editable allocation
  const originalAllocationValue =
    item.amountCents != null ? Number(item.amountCents) / 100 : undefined
  const [allocationValue, setAllocationValue] = useState<number | undefined>(
    originalAllocationValue
  )
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const { bulkUpdateBudgetAllocation } = useBulkUpdateBudgetAllocation({
    budgetId: budget.id,
  })

  // Validation logic
  const budgetAmountCents = budget.amountCents != null ? Number(budget.amountCents) : Infinity

  const isAllocationValid = (value: number | undefined): boolean => {
    if (value === undefined) return true
    return Math.round(value * 100) <= budgetAmountCents
  }

  const hasChanges = allocationValue !== originalAllocationValue
  const canSave = hasChanges && !submitting && isAllocationValid(allocationValue)

  // Handle allocation change
  const handleAllocationChange = (value: number | null) => {
    setAllocationValue(value ?? undefined)
  }

  // Save handler
  const handleSave = async () => {
    if (!canSave) return

    setSubmitting(true)
    try {
      const response = await bulkUpdateBudgetAllocation({
        budgetId: budget.id,
        ids: [item.id],
        amountCents: Math.round((allocationValue ?? 0) * 100),
      })

      const result = handleResult(response, () => {})

      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('budgets.expense_budget_detail.employees.sidepanel.save_success'),
        })

        await apolloClient.refetchQueries({
          include: [
            'FinanceBudgetDetailEmployeesDataCollection',
            'FinanceBudgetDetailRevampPage',
            'FinanceBudgetDetailAmountWidgets',
          ],
        })

        onClose()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  // Format amounts for display
  const reportedAmountCents = Number(item.reportedAmountCents) || 0
  const currentAllocationCents = Math.round((allocationValue ?? 0) * 100)
  const availableAmountCents = currentAllocationCents - reportedAmountCents

  const reportedAmount = amountWithCurrency(reportedAmountCents, budget.currency, i18n.locale)
  const availableAmount = amountWithCurrency(availableAmountCents, budget.currency, i18n.locale)

  // Determine allocation status - Over allocation when allocation exceeds total budget
  const allocationStatus = (() => {
    if (allocationValue === undefined || allocationValue === 0) return null

    // Over allocation when employee allocation exceeds the total budget amount
    if (currentAllocationCents > budgetAmountCents) {
      return {
        text: i18n.t('budgets.allocation_status.over_allocation'),
        variant: 'critical' as const,
      }
    }

    return {
      text: i18n.t('budgets.allocation_status.within_allocation'),
      variant: 'positive' as const,
    }
  })()

  return (
    <>
      <F0Dialog
        isOpen
        onClose={isRemoveModalOpen ? () => {} : onClose}
        position="right"
        title={i18n.t('budgets.expense_budget_detail.employees.sidepanel.title')}
        primaryAction={{
          label: i18n.t('common.save'),
          onClick: handleSave,
          disabled: !canSave,
        }}
        secondaryAction={{
          label: i18n.t('common.cancel'),
          onClick: isRemoveModalOpen ? () => {} : onClose,
        }}
      >
        <F0Box display="flex" flexDirection="column" gap="2xl">
          {/* Employee */}
          {employee && (
            <F0Box display="flex" flexDirection="column" gap="sm" paddingBottom="xl">
              <F0Text variant="label" content={i18n.t('common.employee')} />
              <F0Box display="flex" flexDirection="row" alignItems="center" gap="md">
                <F0AvatarPerson
                  size="md"
                  src={buildUrl(employee.avatar?.url)}
                  firstName={employee.firstName}
                  lastName={employee.lastName}
                />
                <F0Text variant="body" content={employee.fullName} />
              </F0Box>
            </F0Box>
          )}

          {/* Legal Entity */}
          {employee?.legalEntity?.legalName && (
            <F0Box display="flex" flexDirection="column" gap="sm" paddingBottom="xl">
              <F0Text
                variant="label"
                content={i18n.t('budgets.detail.tabs.employees.detail.legal_entity')}
              />
              <F0Text variant="body" content={employee.legalEntity.legalName} />
            </F0Box>
          )}

          {/* Spending Section */}
          <F0Box display="flex" flexDirection="column" gap="lg">
            <F0Heading
              content={i18n.t('budgets.expense_budget_detail.employees.sidepanel.spending_title')}
            />
            <F0Text
              variant="description"
              content={i18n.t(
                'budgets.expense_budget_detail.employees.sidepanel.spending_description'
              )}
            />
            <F0Box display="flex" alignItems="start">
              <F0Link onClick={handleGoToRecords}>
                {i18n.t('budgets.expense_budget_detail.employees.sidepanel.go_to_spendings')}
              </F0Link>
            </F0Box>
          </F0Box>

          {/* Period */}
          {period && (
            <F0Box
              display="flex"
              flexDirection="column"
              gap="sm"
              paddingTop="xl"
              paddingBottom="xl"
            >
              <F0Text
                variant="label"
                content={i18n.t('budgets.expense_budget_detail.employees.sidepanel.period')}
              />
              <F0Text variant="body" content={period} />
            </F0Box>
          )}

          {/* Employee Budget Row */}
          <F0Box display="grid" columns="2" paddingTop="xl">
            {/* Left side: Employee budget status */}
            <F0Box display="flex" flexDirection="column" gap="sm">
              <F0Text
                variant="label"
                content={i18n.t(
                  'budgets.expense_budget_detail.employees.sidepanel.employee_budget'
                )}
              />
              {allocationStatus && (
                <F0TagStatus
                  key={allocationStatus.variant}
                  text={allocationStatus.text}
                  variant={allocationStatus.variant}
                />
              )}
            </F0Box>

            {/* Right side: Allocation input */}
            <F0Box display="flex" flexDirection="column" gap="sm">
              <F0Text
                variant="label"
                content={i18n.t('budgets.expense_budget_detail.employees.sidepanel.allocation')}
              />
              <NumberInput
                label={i18n.t('budgets.expense_budget_detail.employees.sidepanel.allocation')}
                hideLabel
                locale={i18n.locale}
                units={budget.currency}
                value={allocationValue}
                onChange={handleAllocationChange}
                maxDecimals={2}
                size="md"
                error={
                  !isAllocationValid(allocationValue)
                    ? i18n.t(
                        'budgets.expense_budget_detail.employees.sidepanel.validation_exceeds_budget'
                      )
                    : undefined
                }
              />
            </F0Box>
          </F0Box>

          {/* Reported/Available Row */}
          <F0Box display="grid" columns="2" paddingBottom="xl">
            {/* Left side: Reported this period */}
            <F0Box display="flex" flexDirection="column" gap="sm">
              <F0Text
                variant="label"
                content={i18n.t(
                  'budgets.expense_budget_detail.employees.sidepanel.reported_this_period'
                )}
              />
              <F0Text variant="body" content={reportedAmount} />
            </F0Box>

            {/* Right side: Available this period */}
            <F0Box display="flex" flexDirection="column" gap="sm">
              <F0Text
                variant="label"
                content={i18n.t(
                  'budgets.expense_budget_detail.employees.sidepanel.available_this_period'
                )}
              />
              <F0Text variant="body" content={availableAmount} />
            </F0Box>
          </F0Box>

          {/* Remove Section */}
          <F0Box display="flex" flexDirection="column" gap="lg" paddingTop="xl">
            <F0Heading
              content={i18n.t('budgets.expense_budget_detail.employees.sidepanel.remove_title')}
            />
            <F0Text
              variant="description"
              content={i18n.t(
                'budgets.expense_budget_detail.employees.sidepanel.remove_description'
              )}
            />
            <F0Box display="flex" alignItems="start">
              <F0Button
                label={i18n.t('budgets.expense_budget_detail.employees.sidepanel.remove_button')}
                variant="outline"
                onClick={() => setIsRemoveModalOpen(true)}
              />
            </F0Box>
          </F0Box>
        </F0Box>
      </F0Dialog>

      <RemoveEmployeeModal
        isOpen={isRemoveModalOpen}
        item={item}
        budget={budget}
        onClose={() => setIsRemoveModalOpen(false)}
        onSuccess={onClose}
      />
    </>
  )
}
